/**
 * Iter 188 — Reusable @dnd-kit reorderable list.
 * ============================================================================
 *
 * The site-chrome inspectors were using up/down arrow buttons to reorder
 * menu items, buttons, columns, social links, and policy links. That
 * works but feels dated. Replace with proper drag-to-reorder via
 * @dnd-kit/sortable.
 *
 * Public API — designed so the two inspector files only change 4 lines
 * per list:
 *
 *   <Sortable items={items} onReorder={setItems}>
 *     {items.map(it => (
 *       <SortableRow key={it.id} id={it.id}>
 *         {(handleProps) => (
 *           <div className="flex items-center gap-2">
 *             <DragHandle {...handleProps} />
 *             …the existing per-row UI…
 *           </div>
 *         )}
 *       </SortableRow>
 *     ))}
 *   </Sortable>
 *
 * The onReorder callback receives a new items array where the `order`
 * field on each item has already been rewritten to match the new index.
 */
import React from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';


export function Sortable({ items, onReorder, children }) {
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));
  const handleDragEnd = (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((x) => x.id === active.id);
    const newIndex = items.findIndex((x) => x.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const moved = arrayMove(items, oldIndex, newIndex).map((it, i) => ({ ...it, order: i }));
    onReorder(moved);
  };
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((x) => x.id)} strategy={verticalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  );
}


export function SortableRow({ id, children, className = '' }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const handleProps = { ref: setNodeRef, ...attributes, ...listeners };
  // The child is a render-prop that receives handle props to spread on
  // whichever element should be the drag anchor (usually a small grip icon
  // so the rest of the row remains editable).
  const inner = typeof children === 'function'
    ? children({ ...listeners, ...attributes })
    : children;
  return (
    <div ref={setNodeRef} style={style} className={className} data-sortable-id={id}>
      {inner}
    </div>
  );
}


export function DragHandle(props) {
  return (
    <button
      type="button"
      className="w-5 h-5 rounded flex items-center justify-center bg-white/5 hover:bg-white/10 cursor-grab active:cursor-grabbing touch-none"
      title="Drag to reorder"
      {...props}
    >
      <GripVertical size={11} className="text-white/50" />
    </button>
  );
}
